import { normalizeIconPathString } from "../WithdrawPage/withdrawPageHelpers";

export const STAKING_TYPE_LABELS = {
  LOCKED: "Locked Staking",
  FLEXIBLE: "Flexible Staking",
  TOKENIZED: "Tokenized Staking",
};

export const getStakingTypeLabel = (type) => {
  if (!type) return "—";
  const key = String(type).toUpperCase();
  return STAKING_TYPE_LABELS[key] || type;
};

export const getUnbondingPeriodDays = (record) => {
  const pkg = record?.packageId;
  const pkgObj = pkg && typeof pkg === "object" ? pkg : {};
  const raw =
    record?.unbondingPeriodDays ??
    record?.unbondingPeriod ??
    pkgObj.unbondingPeriodDays ??
    pkgObj.unbondingPeriod;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

export const resolvePackageFromPosition = (position) => {
  const pkg = position?.packageId && typeof position.packageId === "object"
    ? position.packageId
    : {};

  return {
    ...pkg,
    currency: pkg.currency || position?.currency,
    currencyFullName: pkg.currencyFullName || position?.currencyFullName,
    _id: pkg._id || pkg.packageId,
    stakingType: pkg.stakingType || position?.stakingType,
    duration: pkg.duration ?? pkg.durationDays ?? position?.durationDays ?? position?.duration,
    durationDays: pkg.durationDays ?? position?.durationDays,
    unbondingPeriodDays: getUnbondingPeriodDays(position),
    earlyWithdrawalPenalty: pkg.earlyWithdrawalPenalty ?? position?.earlyWithdrawalPenalty,
    interestStartsAfterDays: pkg.interestStartsAfterDays ?? position?.interestStartsAfterDays,
    returnPercentage: pkg.returnPercentage ?? pkg.aprMin ?? position?.returnPercentage,
  };
};

export const getPackageCurrencyId = (pkg) => pkg?.currencyId ?? pkg?.currency_id ?? null;

export const parseDec = (val) => {
  if (val == null) return "0";
  if (typeof val === "object" && val.$numberDecimal != null) return val.$numberDecimal;
  return String(val);
};

export const resolveStakeWalletBalance = (res) => {
  const wallet = res?.success && res.data != null ? res.data : res;
  if (!wallet || typeof wallet !== "object") return "0";
  const raw = wallet.balance ?? wallet.available ?? wallet.Balance;
  if (raw == null) return "0";
  return parseDec(raw);
};

export const formatStakeBalance = (balance) => {
  const n = parseFloat(parseDec(balance));
  if (!Number.isFinite(n) || n === 0) return "0.00";
  return n >= 1 ? n.toFixed(2) : n.toFixed(6);
};

export const getTotalInvestedAmount = (record) => {
  const raw = record?.totalInvestedAmount ?? record?.investedAmount ?? "0";
  return parseDec(raw);
};

export const getStakeAmountError = (amount, pkg, availableNum, walletBalance, { skipMaxCheck = false } = {}) => {
  const num = Number(amount);
  if (!amount || !Number.isFinite(num) || num <= 0) return null;

  const minAmount = pkg?.minAmount ?? 0;
  if (num < minAmount) {
    return `Minimum staking amount is ${minAmount} ${pkg.currency}`;
  }

  if (num > availableNum) {
    return `Insufficient balance. Available: ${formatStakeBalance(walletBalance)} ${pkg.currency}`;
  }

  const maxAmount = pkg?.maxAmount;
  if (!skipMaxCheck && maxAmount != null && num > maxAmount) {
    return `Maximum staking amount is ${Number(maxAmount).toLocaleString()} ${pkg.currency}`;
  }

  return null;
};

export const formatDailyReturn = (dailyReturn) => {
  if (dailyReturn >= 0.01) return dailyReturn.toFixed(4);
  if (dailyReturn >= 0.0001) return dailyReturn.toFixed(6);
  return dailyReturn.toFixed(8);
};

export const getReturnAccruesLabel = (interestStartsAfterDays) => {
  const days = Number(interestStartsAfterDays ?? 0);
  return days === 0 ? "Time Subscribed" : `D+${days}`;
};

export const getBonusFirstDistributeDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d} 11:00`;
};

export const formatTierRange = (tier) => {
  const min = tier?.minAmount ?? 0;
  const max = tier?.maxAmount;
  if (max != null) return `${min}-${max}`;
  return `${min}+`;
};

export const getPackageIcon = (pkg) => {
  const fromApi = normalizeIconPathString(pkg?.iconPath);
  if (fromApi) return fromApi;
  const currency = String(pkg?.currency || "").toLowerCase();
  return currency ? `/images/staking_img/${currency}_icon.svg` : "/images/staking_img/usdt_icon.svg";
};

export const getRewardIcon = (pkg) => {
  const fromApi = normalizeIconPathString(pkg?.rewardCurrencyIcon);
  if (fromApi) return fromApi;
  const currency = String(pkg?.rewardCurrency || pkg?.currency || "").toLowerCase();
  return currency ? `/images/staking_img/${currency}_icon.svg` : "/images/staking_img/usdt_icon.svg";
};
